// Proveedor de transporte por defecto para conexiones nuevas.
// Este es el ÚNICO lugar donde el string "baileys" está hardcodeado en el frontend.
// Todos los componentes y hooks deben importar este valor en lugar de escribirlo inline.
export const DEFAULT_PROVIDER = "baileys";

export const PROVIDERS = {
  BAILEYS:   "baileys",
  WABA:      "waba",
  FACEBOOK:  "facebook",
  INSTAGRAM: "instagram",
  TELEGRAM:  "telegram",
  TELEGRAM_USER: "telegram_user",
  // Correo (doc 0445): un buzón que se lee por IMAP y responde por SMTP.
  EMAIL:     "email",
  TIKTOK:    "tiktok",
  // No es un transporte de mensajería (no envía/recibe WhatsApp) — es un
  // formulario de captación (doc 0417) que usa una conexión existente por
  // detrás. Vive en el mismo catálogo solo para que `ChannelIcon` resuelva
  // su logo con el mismo mecanismo que los demás canales.
  WEBFORM:   "webform",
};

/**
 * Devuelve la etiqueta legible del proveedor de canal para mostrar en la UI.
 * Única fuente de verdad — todos los componentes deben importar esta función.
 */
export function getWhatsAppProviderLabel(provider) {
  switch (String(provider || "").toLowerCase()) {
    case "baileys":
    case "beta":      return "API Baileys";
    case "waba":      return "WABA Meta";
    case "facebook":  return "Facebook";
    case "instagram": return "Instagram DM";
    case "telegram":
    case "telegram_user": return "Telegram";
    case "email":     return "Correo";
    case "tiktok":    return "TikTok";
    case "webform":   return "Formulario Web";
    default:          return provider ? String(provider) : "API Baileys";
  }
}

/**
 * Logo del canal, servido desde `public/`.
 *
 * WHY aquí y no en cada componente: el proveedor ya decide su etiqueta en este archivo;
 * el logo es la otra mitad de la misma identidad. Repartir el `switch` por la UI llevaría
 * a que el menú, la tarjeta y el chip del ticket se desincronicen al añadir un canal.
 *
 * WHY archivos locales y no un CDN: la tarjeta de conexión enlazaba el SVG de WhatsApp
 * desde Wikimedia. Un cliente tras un firewall corporativo, o un servidor con salida
 * restringida, se quedaba sin icono en toda la pantalla.
 *
 * Un proveedor desconocido cae en el logo de WhatsApp.
 */
const PROVIDER_ICONS = {
  [PROVIDERS.BAILEYS]:   "/whatsapp.png",
  [PROVIDERS.WABA]:      "/whatsapp-api.png",
  [PROVIDERS.FACEBOOK]:  "/messenger.png",
  [PROVIDERS.INSTAGRAM]: "/instagram.png",
  [PROVIDERS.TELEGRAM]:  "/telegram.png",
  [PROVIDERS.TELEGRAM_USER]: "/telegram.png",
  [PROVIDERS.EMAIL]:     "/email.png",
  [PROVIDERS.WEBFORM]:   "/webform.svg",
};

/**
 * Devuelve la ruta del logo del canal. Ante un proveedor desconocido —o ausente, como
 * cuando el backend aún no lo envía— cae a WhatsApp, que es el canal por defecto: la UI
 * nunca se queda con un hueco.
 */
export function getWhatsAppProviderIcon(provider) {
  const key = String(provider || "").toLowerCase();
  // "beta" es el alias histórico de Baileys; lo resuelve igual que getWhatsAppProviderLabel.
  if (key === "beta") return PROVIDER_ICONS[PROVIDERS.BAILEYS];
  return PROVIDER_ICONS[key] || PROVIDER_ICONS[DEFAULT_PROVIDER];
}

/**
 * Qué permite cada canal en el ticket: el menú del mensaje y la caja de texto preguntan aquí, nunca
 * por el proveedor suelto. Un canal nuevo se declara una vez y toda la pantalla obedece.
 *
 * WHY el correo es distinto (doc 0445 E4.7): un correo enviado no se edita, no se retira y no admite
 * reacciones; no hay notas de voz, ubicación ni encuestas; «reenviar» del panel manda a un contacto
 * por SU canal, no reenvía el correo. Ofrecerlas solo producía el error del servidor.
 */
const CAPACIDADES_CHAT = {
  react: true,
  edit: true,
  delete: true,
  forward: true,
  audio: true,
  richAttachments: true,
  schedule: true,
  campaigns: true,
  signature: "chat",
  thread: false,
};

const CAPACIDADES = {
  [PROVIDERS.EMAIL]: {
    react: false,
    edit: false,
    delete: false,
    forward: false,
    audio: false,
    richAttachments: false,
    // Programar y los recordatorios de la Agenda salen por la conexión de WhatsApp POR DEFECTO
    // (GetDefaultWhatsApp), no por la del ticket: a un contacto de correo le mandarían un WhatsApp.
    schedule: false,
    // Una campaña masiva envía a números de una lista: no tiene sentido por un buzón.
    campaigns: false,
    signature: "email",
    thread: true,
  },
};

export function getChannelCapabilities(provider) {
  return CAPACIDADES[String(provider || "").toLowerCase()] || CAPACIDADES_CHAT;
}

/** Firma del agente según el canal: en un chat, `*Nombre:*` arriba (formato WhatsApp); en un correo, al pie y en texto. */
export function signBody(provider, name, body) {
  if (!name) return body;
  return getChannelCapabilities(provider).signature === "email" ? `${body}\n\n— ${name}` : `*${name}:*\n${body}`;
}

// Razones de desconexión normalizadas (agnósticas al proveedor).
// El backend mapea sus códigos internos (BAD_SESSION, etc.) a estos valores.
// La UI solo debe conocer este enum.
export const DISCONNECT_REASONS = {
  // Valor canónico: DEBE coincidir con MANUAL_DISCONNECT_REASONS del backend
  // (crm-baileys/domain/ReconnectPolicy). Solo con "USER_DISCONNECT" el STOP corta
  // la reconexión automática (isManual) y el backend cierra el transporte del canal.
  USER_DISCONNECT: "USER_DISCONNECT",
  SESSION_CORRUPT: "SESSION_CORRUPT",
  NETWORK_ERROR:   "NETWORK_ERROR",
  SERVER_RESTART:  "SERVER_RESTART",
};
